import { Injectable, inject } from '@angular/core';
import { RestService } from './rest.service';

export interface AppSettings {
  /** Days between mappings. Admin-editable on the server, default 30. */
  scanIntervalDays: number;
}

const DEFAULTS: AppSettings = { scanIntervalDays: 30 };

/**
 * Server-driven app values (surface split part C, Danna 25.09). Read once per
 * page load from the public `app-settings` endpoint; the admin edits them with
 * no build and no store release. Falls back to the server default on failure.
 */
@Injectable({ providedIn: 'root' })
export class AppSettingsService {
  private rest = inject(RestService);
  private pending: Promise<AppSettings> | null = null;

  get(): Promise<AppSettings> {
    if (!this.pending) {
      this.pending = this.rest
        .request<any>({ path: 'app-settings', method: 'GET', skipAuthRedirect: true })
        .then((r) => {
          const n = Number(r?.scanIntervalDays);
          if (Number.isInteger(n) && n > 0) return { scanIntervalDays: n };
          this.pending = null; // failed read: try again next time
          return { ...DEFAULTS };
        })
        .catch(() => {
          this.pending = null;
          return { ...DEFAULTS };
        });
    }
    return this.pending;
  }

  /** Admin write; drops the cached read so the next get() is fresh. */
  async setScanIntervalDays(days: number): Promise<AppSettings> {
    const r = await this.rest.request<any>({
      path: 'admin/app-settings',
      method: 'POST',
      body: { scanIntervalDays: days },
      throwError: true,
    });
    this.pending = null;
    return { scanIntervalDays: Number(r?.scanIntervalDays) || days };
  }
}
